import React from 'react';
import moment from 'moment';
import { Link } from 'react-router-dom';

import { Button, Table } from 'reactstrap';
import TableLoader from 'components/TableLoader';
import Loader from 'components/Loader';

import './RemitosTable.scss';
import { RemitoRaw } from 'types/Cokiba';

interface RemitosTableProps {
  items: RemitoRaw[];
  isLoading: boolean;
  disableGenerators?: boolean;
  onProcessStart: (type: string, deliveryNoteId: number) => void;
  onSendRetry: (deliveryNoteId: number, idObraSocial: number) => void;
  onSendStart: (deliveryNoteId: number) => void;
  onBillingFile: (deliveryNoteId: number, idObraSocial: number) => void;
}

interface ButtonFileProps {
  data: RemitoRaw;
  type: string;
}

interface ButtonFileOptions {
  color: string;
  title: string;
  icon: string;
  generate: boolean;
  loader: boolean;
  send: boolean;
  retry: boolean;
  billingFile: boolean;
  billingFileRunning: boolean;
  retryTitle: string;
}

const insurersWithBillingFile = [
  22, // COMEI
  26, // AMFFA
  36, // AVALIAN
  39, // COESBA
  46, // UP
  47, // CASA
  48, // GALENO
  49, // AMEBPBA
  50, // GALENO DAN
  55, // MEDIFE
  66, // SANCOR
  71, // LA SEGUNDA ART
  74, // FEDERADA
  77, // OSPE
  83, // SMG ART
];

const insurersWithFileSending = [
  71, // LA SEGUNDA ART
  83, // SMG ART
];

export default function RemitosTable(props: RemitosTableProps) {
  const {
    items,
    isLoading,
    onProcessStart,
    onSendRetry,
    disableGenerators = true,
    onSendStart,
    onBillingFile,
  } = props;

  const columns = [
    'Número remito',
    'Mes presentación',
    'Obra social',
    'Dígito obra social',
    'Acciones',
  ];

  const ButtonFile = (props: ButtonFileProps) => {
    const { data, type } = props;

    const options: ButtonFileOptions = {
      color: '',
      title: '',
      icon: '',
      generate: false,
      loader: false,
      send: false,
      retry: false,
      billingFile: false,
      billingFileRunning: false,
      retryTitle: 'Reintentar Envío',
    };

    if (type === 'zip' && data.idObraSocial === 36) {
      options['color'] = 'secondary';
      options['title'] = 'Generar ZIP';
      options['icon'] = 'fa-file-archive';
      options['generate'] = !data.zip;
      options['loader'] = !!data.zip && data.zip.status_id < 6;
      options['send'] = false;

      options['retry'] = false;
      if (options['loader'] && data.zip?.updated_at) {
        const lastUpdate = moment(data.zip.updated_at);
        const days = moment().diff(lastUpdate, 'days');

        if (days >= 1) {
          options['retry'] = true;
        }
      }
    } else if (type === 'pdf' && data.idObraSocial !== 57) {
      options['color'] = 'warning';
      options['title'] = 'Generar PDF';
      options['icon'] = 'fa-file-pdf';
      options['generate'] = !data.pdf;
      options['loader'] = !!data.pdf && data.pdf.status_id < 3;
      options['retry'] = false;
      options['send'] = false;

      if (options['loader'] && data.pdf?.updated_at) {
        const lastUpdate = moment(data.pdf.updated_at);
        const days = moment().diff(lastUpdate, 'days');

        if (days >= 1) {
          options['generate'] = true;
        }
      }
    } else if (type === 'send' && data.idObraSocial === 57) {
      options['color'] = 'danger';
      options['title'] = 'Enviar Documentación';
      options['icon'] = 'fa-file-import';
      options['generate'] = false;
      options['loader'] = !!data.send && data.send.status_id < 2;

      options['retry'] = options['loader'];
      options['retryTitle'] = 'Reiniciar envío';
      options['send'] = !data.send;
    } else if (type === 'billingFile' && insurersWithBillingFile.includes(data.idObraSocial)) {
      options['color'] = 'success';
      options['title'] = 'Regenerar archivo de soporte';
      options['icon'] = 'fa-file-invoice-dollar';
      options['generate'] = false;
      options['billingFileRunning'] = data.billingFileStatus === 'running';
      // Si tiene presentación y no tiene archivo de soporte
      options['billingFile'] = !!data.presentation && !data.billingFile;
    }

    return (
      <>
        {
          options.generate
            ? <Button
                color={ options.color }
                size="sm"
                onClick={ () => onProcessStart(type, data.numeroRemito) }
                disabled={ disableGenerators }
                title={ options.title }
              >
                <i className={`fa ${ options.icon }`}></i>
              </Button>
            : null
        }
        {
          options.retry
            ? <Button
                color="info"
                size="sm"
                onClick={ () => onSendRetry(data.numeroRemito, data.idObraSocial) }
                title={ options.retryTitle }
              >
                <i className="fas fa-redo"></i>
              </Button>
            : null
        }
        {
          options.send
            ? <Button
                color={ options.color }
                size="sm"
                onClick={ () => onSendStart(data.numeroRemito) }
                disabled={ disableGenerators && !options.loader }
                title="Enviar documentación"
              >
                <i className={`fa ${ options.icon }`}></i>
              </Button>
            : null
        }
        { options.loader && <Loader className={`button ${ type }`} /> }
        {
          options.loader && data.pdf ? `${data.pdf.processed} / ${data.pdf.total}` : null
        }

        {
          options.loader && data.zip && !(options.loader && data.pdf)
            ? `${data.zip.processed} / ${data.zip.total}`
            : null
        }

        {
          options.loader && data.send && !(options.loader && data.pdf) && !(options.loader && data.zip)
            ? `${data.send.sent} / ${data.send.total}`
            : null
        }
        {
          options.billingFile && options.billingFileRunning
            ? <Button
                color={ options.color }
                size="sm"
                disabled
                title="Generando archivo de soporte"
              >
                <i className="fas fa-spinner fa-spin"></i>
              </Button>
            : null
        }
        {
          options.billingFile && !options.billingFileRunning
            ? <Button
                color={ options.color }
                size="sm"
                onClick={ () => onBillingFile(data.numeroRemito, data.idObraSocial) }
                disabled={ disableGenerators }
                title={ options.title }
              >
                <i className={`fa ${ options.icon }`}></i>
              </Button>
            : null
        }
      </>
    );
  };

  return (
    <>
      <Table className="align-items-center table-flush" responsive>
        <thead className="thead-light">
          <tr>
            {
              columns.map((colName, i) => (
                <th key={ i } scope='col' className='clickable'>{ colName }</th>
              ))
            }
          </tr>
        </thead>
        <tbody>
          {
            !isLoading ?
              items.length ?
                items.map(row => {
                  return (
                    <tr key={`remito_${ row.numeroRemito }`}>
                      <td>{row.numeroRemito}</td>
                      <td>{row.mesPresentacion}</td>
                      <td>{row.obraSocial}</td>
                      <td>{row.digitoObraSocial || '-'}</td>
                      <td>
                        <Link to={`/admin/remitos/${ row.numeroRemito }`} className="btn btn-round btn-primary btn-sm">
                          <i className="fa fa-eye"></i>
                        </Link>
                        { insurersWithFileSending.includes(row.idObraSocial)
                          ?
                            <>
                              <ButtonFile type="zip" data={ row } />
                              <ButtonFile type="pdf" data={ row } />
                            </>
                          : null
                        }
                        <ButtonFile type="send" data={ row } />
                        { insurersWithBillingFile.includes(row.idObraSocial)
                          ?
                            <ButtonFile type="billingFile" data={ row } />
                          : null
                        }
                      </td>
                    </tr>
                  );
                })
                :
              // content empty
                  <tr>
                    <td colSpan={columns.length + 1} className='text-center'>
                      <span>No se encontraron remitos generados.</span>
                    </td>
                  </tr>
              :
            // loading state
                <tr>
                  <td colSpan={columns.length + 1} className='text-center'>
                    <TableLoader />
                  </td>
                </tr>
          }
        </tbody>
      </Table>
    </>
  );
}
